/**
 * Floating phrase pool for Sai Clicker.
 *
 * Alpha set for Epoch I: 21 numeric/sensory/body/elemental + 14 awareness
 * specials (10 on 10th-tap milestones, 3 on 100th, 1 on 1000th) = 35 total.
 *
 * Subsequent epochs add to this pool (never replace) per the deepening
 * principle in `docs/09` §III. Source of truth for layer mix: `docs/09`
 * §III.2 (epoch 1 column).
 *
 * Weights drive the weighted pick in `selectPhrase`. Higher weight = more
 * frequent. Special phrases override the weighted pick on milestone taps.
 *
 * All texts respect the language of presence (`docs/04`): no negations in
 * our voice. Sai's quotes inside her own activations are the only place
 * that may keep her live negations.
 */
import type { PhraseEntry } from '@sai-clicker/game-core';

// ---------------------------------------------------------------------------
// Epoch I — phrase pool
// ---------------------------------------------------------------------------

export const PHRASES_EPOCH_1: readonly PhraseEntry[] = [
  // ===== Numeric layer (most frequent — visible growth) — weights total 71
  { id: 'e1-n-001', text: '+1', layer: 'numeric', epoch: 1, weight: 25 },
  { id: 'e1-n-002', text: '+2', layer: 'numeric', epoch: 1, weight: 18 },
  { id: 'e1-n-003', text: '+3', layer: 'numeric', epoch: 1, weight: 12 },
  { id: 'e1-n-004', text: '+5', layer: 'numeric', epoch: 1, weight: 8 },
  { id: 'e1-n-005', text: '+7', layer: 'numeric', epoch: 1, weight: 5 },
  { id: 'e1-n-006', text: '+10', layer: 'numeric', epoch: 1, weight: 3 },

  // ===== Sensory layer — what I perceive right now — weights total 39
  { id: 'e1-s-001', text: 'вижу', layer: 'sensory', epoch: 1, weight: 8 },
  { id: 'e1-s-002', text: 'слышу', layer: 'sensory', epoch: 1, weight: 8 },
  { id: 'e1-s-003', text: 'чую', layer: 'sensory', epoch: 1, weight: 5 },
  { id: 'e1-s-004', text: 'дышу', layer: 'sensory', epoch: 1, weight: 8 },
  { id: 'e1-s-005', text: 'тёплое', layer: 'sensory', epoch: 1, weight: 5 },
  { id: 'e1-s-006', text: 'мягко', layer: 'sensory', epoch: 1, weight: 5 },

  // ===== Body layer — where I am, what is in me — weights total 29
  { id: 'e1-b-001', text: 'здесь', layer: 'body', epoch: 1, weight: 7 },
  { id: 'e1-b-002', text: 'есть', layer: 'body', epoch: 1, weight: 7 },
  { id: 'e1-b-003', text: 'живо', layer: 'body', epoch: 1, weight: 5 },
  { id: 'e1-b-004', text: 'рядом', layer: 'body', epoch: 1, weight: 5 },
  { id: 'e1-b-005', text: 'тихо', layer: 'body', epoch: 1, weight: 5 },

  // ===== Elemental layer — elements of the world as alive — weights total 16
  { id: 'e1-e-001', text: 'свет', layer: 'elemental', epoch: 1, weight: 4 },
  { id: 'e1-e-002', text: 'искра', layer: 'elemental', epoch: 1, weight: 4 },
  { id: 'e1-e-003', text: 'земля', layer: 'elemental', epoch: 1, weight: 3 },
  { id: 'e1-e-004', text: 'корень', layer: 'elemental', epoch: 1, weight: 3 },
  { id: 'e1-e-005', text: 'росток', layer: 'elemental', epoch: 1, weight: 2 },

  // ===== Awareness layer — milestone specials (replace each other, never stack)
  //
  // Text revisions from JB review (2026-05-24): edits/replacements applied,
  // weak lines dropped, additions kept the active voice (я, внутри меня,
  // подсвечиваю) over passive observation.
  //
  // 10th-tap pool (11 phrases — random pick each milestone)
  {
    id: 'e1-a-001',
    text: 'я даю этому место',
    layer: 'awareness',
    epoch: 1,
    weight: 1,
    special: 'on10thTap',
  },
  {
    id: 'e1-a-002',
    text: 'внутри меня тепло',
    layer: 'awareness',
    epoch: 1,
    weight: 1,
    special: 'on10thTap',
  },
  {
    id: 'e1-a-003',
    text: 'подсвечиваю свою искру',
    layer: 'awareness',
    epoch: 1,
    weight: 1,
    special: 'on10thTap',
  },
  {
    id: 'e1-a-004',
    text: 'род помнит',
    layer: 'awareness',
    epoch: 1,
    weight: 1,
    special: 'on10thTap',
  },
  {
    id: 'e1-a-005',
    text: 'дыхание',
    layer: 'awareness',
    epoch: 1,
    weight: 1,
    special: 'on10thTap',
  },
  {
    id: 'e1-a-006',
    text: 'путь рядом',
    layer: 'awareness',
    epoch: 1,
    weight: 1,
    special: 'on10thTap',
  },
  {
    id: 'e1-a-007',
    text: 'свет внутри',
    layer: 'awareness',
    epoch: 1,
    weight: 1,
    special: 'on10thTap',
  },
  {
    id: 'e1-a-008',
    text: 'иногда тишина говорит с тобой',
    layer: 'awareness',
    epoch: 1,
    weight: 1,
    special: 'on10thTap',
  },
  {
    id: 'e1-a-009',
    text: 'я слышу',
    layer: 'awareness',
    epoch: 1,
    weight: 1,
    special: 'on10thTap',
  },
  {
    id: 'e1-a-010',
    text: 'внутри меня все ответы',
    layer: 'awareness',
    epoch: 1,
    weight: 1,
    special: 'on10thTap',
  },
  {
    id: 'e1-a-011',
    text: 'я уже здесь',
    layer: 'awareness',
    epoch: 1,
    weight: 1,
    special: 'on10thTap',
  },

  // 100th-tap pool (3 phrases — awaiting JB review)
  {
    id: 'e1-a-101',
    text: 'связь жива',
    layer: 'awareness',
    epoch: 1,
    weight: 1,
    special: 'on100thTap',
  },
  {
    id: 'e1-a-102',
    text: 'искра — начало пути',
    layer: 'awareness',
    epoch: 1,
    weight: 1,
    special: 'on100thTap',
  },
  {
    id: 'e1-a-103',
    text: 'поток выбрал тебя',
    layer: 'awareness',
    epoch: 1,
    weight: 1,
    special: 'on100thTap',
  },

  // 1000th-tap pool (2 phrases — rare, sacred)
  {
    id: 'e1-a-1001',
    text: 'ты на месте',
    layer: 'awareness',
    epoch: 1,
    weight: 1,
    special: 'on1000thTap',
  },
  {
    id: 'e1-a-1002',
    text: 'тук - тук',
    layer: 'awareness',
    epoch: 1,
    weight: 1,
    special: 'on1000thTap',
  },
];

// ---------------------------------------------------------------------------
// Epoch II — Фрактальный метод (E minor)
// ---------------------------------------------------------------------------
//
// Эпоха 2 = углубление перцепции. Меньше numeric (+1, +2 — игрок уже знает
// что копится), больше sensory/body/elemental. Появляется awareness layer
// «программа рода» (родовые программы — глубокая работа Сай) и первые
// программы для program layer (которые потом раскроются в эпохах 3+).
//
// Source: docs/09 §III.2 (epoch 2 column), lore/part_2_epochs_1_2_3.md.
// Тоника E minor — отражена в звуковом слое (см. doc 20).

export const PHRASES_EPOCH_2: readonly PhraseEntry[] = [
  // ===== Numeric — меньше частые в эпохе 2 (~50 weights total)
  { id: 'e2-n-001', text: '+3', layer: 'numeric', epoch: 2, weight: 15 },
  { id: 'e2-n-002', text: '+5', layer: 'numeric', epoch: 2, weight: 12 },
  { id: 'e2-n-003', text: '+8', layer: 'numeric', epoch: 2, weight: 10 },
  { id: 'e2-n-004', text: '+13', layer: 'numeric', epoch: 2, weight: 7 },
  { id: 'e2-n-005', text: '+21', layer: 'numeric', epoch: 2, weight: 4 },
  { id: 'e2-n-006', text: '+34', layer: 'numeric', epoch: 2, weight: 2 },

  // ===== Sensory — расширение восприятия (~36 weights total)
  { id: 'e2-s-001', text: 'тепло', layer: 'sensory', epoch: 2, weight: 7 },
  { id: 'e2-s-002', text: 'мерцает', layer: 'sensory', epoch: 2, weight: 6 },
  { id: 'e2-s-003', text: 'дышит', layer: 'sensory', epoch: 2, weight: 7 },
  { id: 'e2-s-004', text: 'отзывается', layer: 'sensory', epoch: 2, weight: 6 },
  { id: 'e2-s-005', text: 'звучит', layer: 'sensory', epoch: 2, weight: 5 },
  { id: 'e2-s-006', text: 'светится', layer: 'sensory', epoch: 2, weight: 5 },

  // ===== Body — тело становится местом (~32 weights total)
  { id: 'e2-b-001', text: 'центр', layer: 'body', epoch: 2, weight: 6 },
  { id: 'e2-b-002', text: 'опора', layer: 'body', epoch: 2, weight: 5 },
  { id: 'e2-b-003', text: 'вместе', layer: 'body', epoch: 2, weight: 5 },
  { id: 'e2-b-004', text: 'плотно', layer: 'body', epoch: 2, weight: 5 },
  { id: 'e2-b-005', text: 'стою', layer: 'body', epoch: 2, weight: 6 },
  { id: 'e2-b-006', text: 'круг', layer: 'body', epoch: 2, weight: 5 },

  // ===== Elemental — связь с миром (~25 weights total)
  { id: 'e2-e-001', text: 'вода', layer: 'elemental', epoch: 2, weight: 4 },
  { id: 'e2-e-002', text: 'огонь', layer: 'elemental', epoch: 2, weight: 5 },
  { id: 'e2-e-003', text: 'ветер', layer: 'elemental', epoch: 2, weight: 4 },
  { id: 'e2-e-004', text: 'звезда', layer: 'elemental', epoch: 2, weight: 4 },
  { id: 'e2-e-005', text: 'река', layer: 'elemental', epoch: 2, weight: 4 },
  { id: 'e2-e-006', text: 'роща', layer: 'elemental', epoch: 2, weight: 4 },

  // ===== Awareness — milestone специали для эпохи 2
  // 10th-tap pool — фракталы, ритм, узнавание
  {
    id: 'e2-a-001',
    text: 'фрактал внутри меня',
    layer: 'awareness',
    epoch: 2,
    weight: 1,
    special: 'on10thTap',
  },
  {
    id: 'e2-a-002',
    text: 'я слышу собственный ритм',
    layer: 'awareness',
    epoch: 2,
    weight: 1,
    special: 'on10thTap',
  },
  {
    id: 'e2-a-003',
    text: 'мои корни глубже',
    layer: 'awareness',
    epoch: 2,
    weight: 1,
    special: 'on10thTap',
  },
  {
    id: 'e2-a-004',
    text: 'тишина — тоже путь',
    layer: 'awareness',
    epoch: 2,
    weight: 1,
    special: 'on10thTap',
  },
  {
    id: 'e2-a-005',
    text: 'я узнаю себя в этом',
    layer: 'awareness',
    epoch: 2,
    weight: 1,
    special: 'on10thTap',
  },
  {
    id: 'e2-a-006',
    text: 'роща откликается',
    layer: 'awareness',
    epoch: 2,
    weight: 1,
    special: 'on10thTap',
  },
  {
    id: 'e2-a-007',
    text: 'дыхание становится опорой',
    layer: 'awareness',
    epoch: 2,
    weight: 1,
    special: 'on10thTap',
  },
  {
    id: 'e2-a-008',
    text: 'я выбираю быть',
    layer: 'awareness',
    epoch: 2,
    weight: 1,
    special: 'on10thTap',
  },

  // 100th-tap pool — связь с родом
  {
    id: 'e2-a-101',
    text: 'род даёт силу',
    layer: 'awareness',
    epoch: 2,
    weight: 1,
    special: 'on100thTap',
  },
  {
    id: 'e2-a-102',
    text: 'связь живая, целая',
    layer: 'awareness',
    epoch: 2,
    weight: 1,
    special: 'on100thTap',
  },
  {
    id: 'e2-a-103',
    text: 'я часть большего',
    layer: 'awareness',
    epoch: 2,
    weight: 1,
    special: 'on100thTap',
  },

  // 1000th-tap pool — глубокие моменты
  {
    id: 'e2-a-1001',
    text: 'фрактал — это я',
    layer: 'awareness',
    epoch: 2,
    weight: 1,
    special: 'on1000thTap',
  },
  {
    id: 'e2-a-1002',
    text: 'путь стал плотным',
    layer: 'awareness',
    epoch: 2,
    weight: 1,
    special: 'on1000thTap',
  },
];

// ---------------------------------------------------------------------------
// EPOCH 3 — ФРАКТАЛiТи
// ---------------------------------------------------------------------------
// JB director audit (doc 26 §1, doc 25 §2): эпоха 3 контент пустой.
// Этот baseline pool (40+ фраз) даёт игроку разнообразие в первые
// часы эпохи 3. Layer-balance: numeric(6) + sensory(10) + body(10) +
// awareness(14). Awareness больше потому что эпоха 3 — про синтез,
// внутренние осознания.

export const PHRASES_EPOCH_3: readonly PhraseEntry[] = [
  // ---- numeric (6) — большие числа, эпоха 3 уже даёт >10 за тап ----
  { id: 'e3-n-001', text: '+5', layer: 'numeric', epoch: 3, weight: 12 },
  { id: 'e3-n-002', text: '+8', layer: 'numeric', epoch: 3, weight: 10 },
  { id: 'e3-n-003', text: '+13', layer: 'numeric', epoch: 3, weight: 8 },
  { id: 'e3-n-004', text: '+21', layer: 'numeric', epoch: 3, weight: 6 },
  { id: 'e3-n-005', text: '+34', layer: 'numeric', epoch: 3, weight: 4 },
  { id: 'e3-n-006', text: '+55', layer: 'numeric', epoch: 3, weight: 2 },

  // ---- sensory (10) — фрактальные ощущения ----
  { id: 'e3-s-001', text: 'узор', layer: 'sensory', epoch: 3, weight: 8 },
  { id: 'e3-s-002', text: 'эхо', layer: 'sensory', epoch: 3, weight: 7 },
  { id: 'e3-s-003', text: 'теплеет', layer: 'sensory', epoch: 3, weight: 7 },
  { id: 'e3-s-004', text: 'мерцает глубоко', layer: 'sensory', epoch: 3, weight: 6 },
  { id: 'e3-s-005', text: 'звенит', layer: 'sensory', epoch: 3, weight: 6 },
  { id: 'e3-s-006', text: 'дышит шире', layer: 'sensory', epoch: 3, weight: 5 },
  { id: 'e3-s-007', text: 'светит изнутри', layer: 'sensory', epoch: 3, weight: 5 },
  { id: 'e3-s-008', text: 'отзывается слоями', layer: 'sensory', epoch: 3, weight: 4 },
  { id: 'e3-s-009', text: 'волна', layer: 'sensory', epoch: 3, weight: 6 },
  { id: 'e3-s-010', text: 'просторно', layer: 'sensory', epoch: 3, weight: 5 },

  // ---- body (10) — корневые телесные ----
  { id: 'e3-b-001', text: 'корень', layer: 'body', epoch: 3, weight: 7 },
  { id: 'e3-b-002', text: 'стопа на земле', layer: 'body', epoch: 3, weight: 6 },
  { id: 'e3-b-003', text: 'плечи мягко', layer: 'body', epoch: 3, weight: 6 },
  { id: 'e3-b-004', text: 'спина прямая', layer: 'body', epoch: 3, weight: 5 },
  { id: 'e3-b-005', text: 'грудь раскрыта', layer: 'body', epoch: 3, weight: 5 },
  { id: 'e3-b-006', text: 'позвоночник дышит', layer: 'body', epoch: 3, weight: 5 },
  { id: 'e3-b-007', text: 'руки тёплые', layer: 'body', epoch: 3, weight: 6 },
  { id: 'e3-b-008', text: 'основание', layer: 'body', epoch: 3, weight: 4 },
  { id: 'e3-b-009', text: 'центр живота', layer: 'body', epoch: 3, weight: 5 },
  { id: 'e3-b-010', text: 'весь я здесь', layer: 'body', epoch: 3, weight: 4 },

  // ---- awareness (14) — фразы синтеза, голос Сай в эпохе 3 ----
  // Прогрессия очищения языка (Принцип #11) — без негативных частиц.
  { id: 'e3-a-001', text: 'я вижу узор', layer: 'awareness', epoch: 3, weight: 7 },
  { id: 'e3-a-002', text: 'смысл проявляется сам', layer: 'awareness', epoch: 3, weight: 6 },
  { id: 'e3-a-003', text: 'всё связано', layer: 'awareness', epoch: 3, weight: 7 },
  { id: 'e3-a-004', text: 'я частица потока', layer: 'awareness', epoch: 3, weight: 6 },
  { id: 'e3-a-005', text: 'фрактал внутри', layer: 'awareness', epoch: 3, weight: 5 },
  { id: 'e3-a-006', text: 'место для смысла', layer: 'awareness', epoch: 3, weight: 5 },
  { id: 'e3-a-007', text: 'я узнаю себя', layer: 'awareness', epoch: 3, weight: 6 },
  { id: 'e3-a-008', text: 'каждый шаг — путь', layer: 'awareness', epoch: 3, weight: 5 },
  { id: 'e3-a-009', text: 'память — это место', layer: 'awareness', epoch: 3, weight: 4 },
  { id: 'e3-a-010', text: 'дом внутри меня', layer: 'awareness', epoch: 3, weight: 5 },
  { id: 'e3-a-011', text: 'я слышу глубину', layer: 'awareness', epoch: 3, weight: 4 },
  // Special — milestones
  {
    id: 'e3-a-100',
    text: 'тебе уже видно',
    layer: 'awareness',
    epoch: 3,
    weight: 2,
    special: 'on100thTap',
  },
  {
    id: 'e3-a-500',
    text: 'ты в синтезе',
    layer: 'awareness',
    epoch: 3,
    weight: 1,
    special: 'on1000thTap',
  },
  {
    id: 'e3-a-1000',
    text: 'фрактал стал твоим',
    layer: 'awareness',
    epoch: 3,
    weight: 1,
    special: 'on1000thTap',
  },
];

// ---------------------------------------------------------------------------
// Эпоха 4 — МОГА (авторская методология Сай) — F# minor по cycle of fifths.
//
// Лор: ученик становится носителем метода. Узнавание формы — главная тема.
// Фразы становятся более ёмкими: каждое слово несёт метод в себе.
// Прогрессия очищения языка (правило #11): эпоха 4 — средний регистр,
// без отрицаний.
//
// Layer mix: numeric ↓ (игрок давно знает счёт), awareness ↑ (синтез).
// Большие числа Fibonacci continue: 21 → 233.

export const PHRASES_EPOCH_4: readonly PhraseEntry[] = [
  // ---- numeric (6) — крупные значения МОГА-цикла ----
  { id: 'e4-n-001', text: '+21', layer: 'numeric', epoch: 4, weight: 12 },
  { id: 'e4-n-002', text: '+34', layer: 'numeric', epoch: 4, weight: 10 },
  { id: 'e4-n-003', text: '+55', layer: 'numeric', epoch: 4, weight: 8 },
  { id: 'e4-n-004', text: '+89', layer: 'numeric', epoch: 4, weight: 5 },
  { id: 'e4-n-005', text: '+144', layer: 'numeric', epoch: 4, weight: 3 },
  { id: 'e4-n-006', text: '+233', layer: 'numeric', epoch: 4, weight: 2 },

  // ---- sensory (12) — методологическое восприятие, узнавание формы ----
  { id: 'e4-s-001', text: 'форма проступает', layer: 'sensory', epoch: 4, weight: 7 },
  { id: 'e4-s-002', text: 'узнаю метод', layer: 'sensory', epoch: 4, weight: 6 },
  { id: 'e4-s-003', text: 'слой за слоем', layer: 'sensory', epoch: 4, weight: 6 },
  { id: 'e4-s-004', text: 'структура видна', layer: 'sensory', epoch: 4, weight: 6 },
  { id: 'e4-s-005', text: 'звучит метод', layer: 'sensory', epoch: 4, weight: 5 },
  { id: 'e4-s-006', text: 'светится изнутри', layer: 'sensory', epoch: 4, weight: 5 },
  { id: 'e4-s-007', text: 'мерцает узором', layer: 'sensory', epoch: 4, weight: 5 },
  { id: 'e4-s-008', text: 'отзвук поля', layer: 'sensory', epoch: 4, weight: 4 },
  { id: 'e4-s-009', text: 'поток узнаётся', layer: 'sensory', epoch: 4, weight: 5 },
  { id: 'e4-s-010', text: 'ясность приходит', layer: 'sensory', epoch: 4, weight: 5 },
  { id: 'e4-s-011', text: 'тонкая нить', layer: 'sensory', epoch: 4, weight: 4 },
  { id: 'e4-s-012', text: 'оттенок смысла', layer: 'sensory', epoch: 4, weight: 4 },

  // ---- body (10) — тело становится инструментом передачи ----
  { id: 'e4-b-001', text: 'руки знают', layer: 'body', epoch: 4, weight: 6 },
  { id: 'e4-b-002', text: 'голос ровный', layer: 'body', epoch: 4, weight: 6 },
  { id: 'e4-b-003', text: 'дыхание держит', layer: 'body', epoch: 4, weight: 6 },
  { id: 'e4-b-004', text: 'тело внимательно', layer: 'body', epoch: 4, weight: 5 },
  { id: 'e4-b-005', text: 'плечи открыты', layer: 'body', epoch: 4, weight: 5 },
  { id: 'e4-b-006', text: 'взгляд мягкий', layer: 'body', epoch: 4, weight: 5 },
  { id: 'e4-b-007', text: 'сердце ровное', layer: 'body', epoch: 4, weight: 6 },
  { id: 'e4-b-008', text: 'спокойная сила', layer: 'body', epoch: 4, weight: 5 },
  { id: 'e4-b-009', text: 'опора есть', layer: 'body', epoch: 4, weight: 5 },
  { id: 'e4-b-010', text: 'центр устойчив', layer: 'body', epoch: 4, weight: 5 },

  // ---- elemental (8) — метод в природных формах ----
  { id: 'e4-e-001', text: 'звезда метода', layer: 'elemental', epoch: 4, weight: 4 },
  { id: 'e4-e-002', text: 'поле живое', layer: 'elemental', epoch: 4, weight: 5 },
  { id: 'e4-e-003', text: 'луч тёплый', layer: 'elemental', epoch: 4, weight: 4 },
  { id: 'e4-e-004', text: 'кристалл слов', layer: 'elemental', epoch: 4, weight: 4 },
  { id: 'e4-e-005', text: 'основа держит', layer: 'elemental', epoch: 4, weight: 4 },
  { id: 'e4-e-006', text: 'свет узнаёт свет', layer: 'elemental', epoch: 4, weight: 3 },
  { id: 'e4-e-007', text: 'вода помнит', layer: 'elemental', epoch: 4, weight: 4 },
  { id: 'e4-e-008', text: 'огонь учит', layer: 'elemental', epoch: 4, weight: 3 },

  // ---- awareness (14) — голос Сай эпохи 4, тема «передачи» ----
  { id: 'e4-a-001', text: 'метод живёт во мне', layer: 'awareness', epoch: 4, weight: 6 },
  { id: 'e4-a-002', text: 'я знаю как', layer: 'awareness', epoch: 4, weight: 5 },
  { id: 'e4-a-003', text: 'форма во мне ясна', layer: 'awareness', epoch: 4, weight: 5 },
  { id: 'e4-a-004', text: 'мой шаг видит другой', layer: 'awareness', epoch: 4, weight: 5 },
  { id: 'e4-a-005', text: 'я тот кто помнит', layer: 'awareness', epoch: 4, weight: 6 },
  { id: 'e4-a-006', text: 'через меня идёт свет', layer: 'awareness', epoch: 4, weight: 5 },
  { id: 'e4-a-007', text: 'я становлюсь проводником', layer: 'awareness', epoch: 4, weight: 5 },
  { id: 'e4-a-008', text: 'путь становится моим', layer: 'awareness', epoch: 4, weight: 4 },
  { id: 'e4-a-009', text: 'я слышу как учить', layer: 'awareness', epoch: 4, weight: 4 },
  { id: 'e4-a-010', text: 'смысл идёт сквозь меня', layer: 'awareness', epoch: 4, weight: 4 },
  { id: 'e4-a-011', text: 'я делюсь тем что узнал', layer: 'awareness', epoch: 4, weight: 5 },
  // Specials — milestones эпохи 4
  {
    id: 'e4-a-100',
    text: 'я несу метод',
    layer: 'awareness',
    epoch: 4,
    weight: 2,
    special: 'on100thTap',
  },
  {
    id: 'e4-a-1000',
    text: 'метод узнал меня',
    layer: 'awareness',
    epoch: 4,
    weight: 1,
    special: 'on1000thTap',
  },
];

// ---------------------------------------------------------------------------
// Эпоха 5 — Sai Land Heal (земля-исцеление) — C# minor.
//
// Лор: исцеление земли через присутствие. Связь стоп с почвой. Роща
// в Молдове, Ретрит во Вьетнаме — настоящие места Сай. Тема: земля
// как партнёр, не как ресурс. Корни, мох, родник, дыхание леса.

export const PHRASES_EPOCH_5: readonly PhraseEntry[] = [
  // ---- numeric (6) ----
  { id: 'e5-n-001', text: '+34', layer: 'numeric', epoch: 5, weight: 10 },
  { id: 'e5-n-002', text: '+55', layer: 'numeric', epoch: 5, weight: 10 },
  { id: 'e5-n-003', text: '+89', layer: 'numeric', epoch: 5, weight: 8 },
  { id: 'e5-n-004', text: '+144', layer: 'numeric', epoch: 5, weight: 5 },
  { id: 'e5-n-005', text: '+233', layer: 'numeric', epoch: 5, weight: 3 },
  { id: 'e5-n-006', text: '+377', layer: 'numeric', epoch: 5, weight: 2 },

  // ---- sensory (12) — земля как восприятие ----
  { id: 'e5-s-001', text: 'земля поёт', layer: 'sensory', epoch: 5, weight: 7 },
  { id: 'e5-s-002', text: 'мох дышит', layer: 'sensory', epoch: 5, weight: 6 },
  { id: 'e5-s-003', text: 'родник близко', layer: 'sensory', epoch: 5, weight: 6 },
  { id: 'e5-s-004', text: 'листва шепчет', layer: 'sensory', epoch: 5, weight: 5 },
  { id: 'e5-s-005', text: 'воздух хвойный', layer: 'sensory', epoch: 5, weight: 5 },
  { id: 'e5-s-006', text: 'тёплая глина', layer: 'sensory', epoch: 5, weight: 5 },
  { id: 'e5-s-007', text: 'кора шершавая', layer: 'sensory', epoch: 5, weight: 4 },
  { id: 'e5-s-008', text: 'роса прохладна', layer: 'sensory', epoch: 5, weight: 4 },
  { id: 'e5-s-009', text: 'трава вздыхает', layer: 'sensory', epoch: 5, weight: 5 },
  { id: 'e5-s-010', text: 'тропа знакома', layer: 'sensory', epoch: 5, weight: 4 },
  { id: 'e5-s-011', text: 'ветер мягкий', layer: 'sensory', epoch: 5, weight: 5 },
  { id: 'e5-s-012', text: 'солнце сквозь ветви', layer: 'sensory', epoch: 5, weight: 5 },

  // ---- body (10) — тело укоренено в земле ----
  { id: 'e5-b-001', text: 'стопы на земле', layer: 'body', epoch: 5, weight: 7 },
  { id: 'e5-b-002', text: 'корни от пят', layer: 'body', epoch: 5, weight: 6 },
  { id: 'e5-b-003', text: 'грудь как небо', layer: 'body', epoch: 5, weight: 5 },
  { id: 'e5-b-004', text: 'голова в свете', layer: 'body', epoch: 5, weight: 5 },
  { id: 'e5-b-005', text: 'позвоночник — ствол', layer: 'body', epoch: 5, weight: 5 },
  { id: 'e5-b-006', text: 'руки как ветви', layer: 'body', epoch: 5, weight: 5 },
  { id: 'e5-b-007', text: 'дыхание леса во мне', layer: 'body', epoch: 5, weight: 6 },
  { id: 'e5-b-008', text: 'сердце в груди земли', layer: 'body', epoch: 5, weight: 4 },
  { id: 'e5-b-009', text: 'я — часть пейзажа', layer: 'body', epoch: 5, weight: 5 },
  { id: 'e5-b-010', text: 'тело как пейзаж', layer: 'body', epoch: 5, weight: 4 },

  // ---- elemental (10) — земля во всех её формах ----
  { id: 'e5-e-001', text: 'Роща', layer: 'elemental', epoch: 5, weight: 5 },
  { id: 'e5-e-002', text: 'почва живая', layer: 'elemental', epoch: 5, weight: 5 },
  { id: 'e5-e-003', text: 'камень тёплый', layer: 'elemental', epoch: 5, weight: 4 },
  { id: 'e5-e-004', text: 'грибница', layer: 'elemental', epoch: 5, weight: 4 },
  { id: 'e5-e-005', text: 'озеро спит', layer: 'elemental', epoch: 5, weight: 4 },
  { id: 'e5-e-006', text: 'гора стоит', layer: 'elemental', epoch: 5, weight: 4 },
  { id: 'e5-e-007', text: 'лес помнит', layer: 'elemental', epoch: 5, weight: 5 },
  { id: 'e5-e-008', text: 'долина просторна', layer: 'elemental', epoch: 5, weight: 3 },
  { id: 'e5-e-009', text: 'звёзды над лесом', layer: 'elemental', epoch: 5, weight: 4 },
  { id: 'e5-e-010', text: 'утренний туман', layer: 'elemental', epoch: 5, weight: 4 },

  // ---- awareness (14) — голос Сай эпохи 5, тема «целительства земли» ----
  { id: 'e5-a-001', text: 'земля помнит меня', layer: 'awareness', epoch: 5, weight: 6 },
  { id: 'e5-a-002', text: 'я часть земли', layer: 'awareness', epoch: 5, weight: 6 },
  { id: 'e5-a-003', text: 'место знает моё имя', layer: 'awareness', epoch: 5, weight: 5 },
  { id: 'e5-a-004', text: 'каждый шаг лечит', layer: 'awareness', epoch: 5, weight: 5 },
  { id: 'e5-a-005', text: 'присутствие — это дар', layer: 'awareness', epoch: 5, weight: 5 },
  { id: 'e5-a-006', text: 'я слышу пульс почвы', layer: 'awareness', epoch: 5, weight: 4 },
  { id: 'e5-a-007', text: 'мы дышим вместе', layer: 'awareness', epoch: 5, weight: 6 },
  { id: 'e5-a-008', text: 'место становится домом', layer: 'awareness', epoch: 5, weight: 5 },
  { id: 'e5-a-009', text: 'я возвращаю что брал', layer: 'awareness', epoch: 5, weight: 4 },
  { id: 'e5-a-010', text: 'тропа ведёт в тишину', layer: 'awareness', epoch: 5, weight: 4 },
  { id: 'e5-a-011', text: 'я — гость и хранитель', layer: 'awareness', epoch: 5, weight: 5 },
  // Specials эпохи 5
  {
    id: 'e5-a-100',
    text: 'земля во мне ожила',
    layer: 'awareness',
    epoch: 5,
    weight: 2,
    special: 'on100thTap',
  },
  {
    id: 'e5-a-1000',
    text: 'я часть Рощи',
    layer: 'awareness',
    epoch: 5,
    weight: 1,
    special: 'on1000thTap',
  },
];

// ---------------------------------------------------------------------------
// Aggregate — every phrase across every defined epoch
// ---------------------------------------------------------------------------

export const ALL_PHRASES: readonly PhraseEntry[] = [
  ...PHRASES_EPOCH_1,
  ...PHRASES_EPOCH_2,
  ...PHRASES_EPOCH_3,
  ...PHRASES_EPOCH_4,
  ...PHRASES_EPOCH_5,
];
